/**
 * InternalCallScreen - Elefa-to-Elefa E2EE voice/video (free, Signal Protocol).
 *
 * Counterpart: PstnCallScreen (paid PSTN dial via IDT/ASTPP).
 * Shared primitives live in ./_call-shared/. Do NOT merge the two screens.
 *
 * Renamed from ElefaCallScreen on 2026-05-15.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { Phone, PhoneOff, Mic, MicOff, Video, VideoOff, MessageCircle, MoreVertical, ArrowLeft, Monitor, MonitorOff, Volume2, VolumeX, Pause, Play, RotateCcw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChatPanel } from '@/components/chat/ChatPanel';
import { CallQualityIndicator } from './CallQualityIndicator';
import { CallReconnectingOverlay } from './CallReconnectingOverlay';
import { SafetyNumberModal } from './SafetyNumberModal';
import { supabase } from '@/integrations/supabase/client';
import type { CallQuality } from '@/lib/call-quality-monitor';
import { formatCallDuration } from '@/components/connect/_call-shared/useCallDuration';
// useRemoteStreamAttach is available in _call-shared/ - left inline here
// because the working stream-attach path is delicate; consolidate later.

interface InternalCallScreenProps {
  callerName: string;
  callerAvatar: string | null;
  status: 'ringing' | 'connecting' | 'connected';
  isVideo: boolean;
  isIncoming: boolean;
  duration: number;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  isMuted: boolean;
  isVideoEnabled: boolean;
  onEndCall: () => void;
  onToggleMute: () => void;
  onToggleVideo: () => void;
  calleeId?: string;
  callQuality?: CallQuality;
  isReconnecting?: boolean;
  isScreenSharing?: boolean;
  onToggleScreenShare?: () => void;
  isSpeakerOn?: boolean;
  onToggleSpeaker?: () => void;
  isOnHold?: boolean;
  isRemoteOnHold?: boolean;
  onToggleHold?: () => void;
  onFlipCamera?: () => void;
}

export function InternalCallScreen({
  callerName,
  callerAvatar,
  status,
  isVideo,
  isIncoming,
  duration,
  localStream,
  remoteStream,
  isMuted,
  isVideoEnabled,
  onEndCall,
  onToggleMute,
  onToggleVideo,
  calleeId,
  callQuality,
  isReconnecting = false,
  isScreenSharing = false,
  onToggleScreenShare,
  isSpeakerOn = false,
  onToggleSpeaker,
  isOnHold = false,
  isRemoteOnHold = false,
  onToggleHold,
  onFlipCamera,
}: InternalCallScreenProps) {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [chatRoomId, setChatRoomId] = useState<string | null>(null);
  const [showSafetyNumber, setShowSafetyNumber] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const localVideoRef = useRef<HTMLVideoElement>(null);

  // Get current user for safety number
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setCurrentUserId(data.session?.user?.id || null);
    });
  }, []);

  // Find existing DM room with the callee
  useEffect(() => {
    if (!calleeId) return;
    const findRoom = async () => {
      // #9 FIX: Use getSession (local) instead of getUser (network call)
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) return;

      const { data: myRooms } = await supabase
        .from('chat_room_members')
        .select('room_id')
        .eq('user_id', user.id);

      if (!myRooms?.length) return;

      const { data: shared } = await supabase
        .from('chat_room_members')
        .select('room_id')
        .eq('user_id', calleeId)
        .in('room_id', myRooms.map(r => r.room_id))
        .limit(1)
        .maybeSingle();

      if (shared) setChatRoomId(shared.room_id);
    };
    findRoom();
  }, [calleeId]);

  const handleBack = useCallback(() => {
    setIsMinimized(true);
  }, []);

  // Detect desktop for screen share button
  const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 768;

  // ─── Attach remote audio/video stream ──────────────────────────────────
  const remoteAudioRef = useRef<HTMLAudioElement>(null);

  // Attach remote stream + cleanup srcObject on unmount/stream change
  // CRITICAL: For video calls, audio plays through the <video> element.
  // The hidden <audio> is ONLY for audio-only calls to avoid double output.
  useEffect(() => {
    if (!remoteStream) {
      if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
      if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
      return;
    }

    const tryPlay = (el: HTMLMediaElement, label: string) => {
      // Attempt immediate play
      const attemptPlay = () => {
        el.play().catch((err) => {
          console.warn(`[ElefaCall] ${label} autoplay blocked:`, err.message);
          // Retry on next user gesture
          const resumeOnGesture = () => {
            el.play().catch(() => {});
            document.removeEventListener('click', resumeOnGesture);
            document.removeEventListener('touchstart', resumeOnGesture);
          };
          document.addEventListener('click', resumeOnGesture, { once: true });
          document.addEventListener('touchstart', resumeOnGesture, { once: true });
        });
      };
      attemptPlay();
      // Retry after a short delay (handles race between stream readiness and play)
      setTimeout(attemptPlay, 200);
      setTimeout(attemptPlay, 1000);
    };

    if (isVideo && remoteVideoRef.current) {
      // Video element handles BOTH video and audio
      remoteVideoRef.current.srcObject = remoteStream;
      remoteVideoRef.current.muted = false;
      remoteVideoRef.current.volume = 1.0;
      tryPlay(remoteVideoRef.current, 'remote-video');

      // Mute hidden audio to prevent double audio output
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = remoteStream;
        remoteAudioRef.current.muted = true;
        remoteAudioRef.current.volume = 0;
      }
    } else {
      // Audio-only call: play through BOTH hidden audio AND video element as fallback
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = remoteStream;
        remoteAudioRef.current.muted = false;
        remoteAudioRef.current.volume = 1.0;
        tryPlay(remoteAudioRef.current, 'remote-audio');
      }
      // Also attach to video ref as backup (some browsers handle video element audio better)
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = remoteStream;
        remoteVideoRef.current.muted = false;
        remoteVideoRef.current.volume = 1.0;
        tryPlay(remoteVideoRef.current, 'remote-audio-via-video');
      }
    }

    const remoteVideoEl = remoteVideoRef.current;
    const remoteAudioEl = remoteAudioRef.current;
    return () => {
      if (remoteVideoEl) remoteVideoEl.srcObject = null;
      if (remoteAudioEl) remoteAudioEl.srcObject = null;
    };
  }, [remoteStream, isVideo]);

  // Speaker routing is now handled by the hook's toggleSpeaker (setSinkId + device enumeration)

  useEffect(() => {
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = localStream || null;
    }
    const localVideoEl = localVideoRef.current;
    return () => {
      if (localVideoEl) localVideoEl.srcObject = null;
    };
  }, [localStream]);

  const formatDuration = formatCallDuration;

  const getStatusText = (): string => {
    switch (status) {
      case 'ringing':
        return isIncoming ? 'Incoming call...' : 'Ringing...';
      case 'connecting':
        return 'Connecting...';
      case 'connected':
        return formatDuration(duration);
      default:
        return '';
    }
  };

  // ─── Minimized floating pill ───
  if (isMinimized && status === 'connected') {
    return (
      <motion.div
        drag
        dragMomentum={false}
        dragElastic={0.1}
        initial={{ opacity: 0, scale: 0.8, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.8 }}
        transition={{ type: 'spring', stiffness: 400, damping: 28 }}
        className="fixed bottom-28 right-4 z-50 cursor-grab active:cursor-grabbing"
        style={{ touchAction: 'none' }}
      >
        <div
          className="flex items-center gap-2.5 bg-card/95 backdrop-blur-xl border border-border/50 rounded-full pl-1.5 pr-2 py-1.5 shadow-elevated"
          onClick={() => setIsMinimized(false)}
        >
          <Avatar className="h-10 w-10 border-2 border-primary/30">
            <AvatarImage src={callerAvatar || undefined} alt={callerName} />
            <AvatarFallback className="bg-primary/20 text-primary text-sm font-semibold">
              {callerName.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col min-w-0">
            <span className="text-xs font-semibold truncate max-w-[80px]">{callerName}</span>
            <span className="text-[11px] text-primary tabular-nums">{formatDuration(duration)}</span>
          </div>
          <div className="flex items-center gap-1 ml-1">
            <Button
              variant="ghost"
              size="icon"
              className={`h-8 w-8 rounded-full ${isMuted ? 'bg-destructive/15 text-destructive' : 'text-muted-foreground'}`}
              onClick={(e) => { e.stopPropagation(); onToggleMute(); }}
            >
              {isMuted ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
            </Button>
            <Button
              variant="destructive"
              size="icon"
              className="h-8 w-8 rounded-full"
              onClick={(e) => { e.stopPropagation(); onEndCall(); }}
            >
              <PhoneOff className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </motion.div>
    );
  }

  // ─── Full-screen call UI - Telegram-style vibrant gradient ───
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex flex-col overflow-hidden"
    >
      <audio ref={remoteAudioRef} autoPlay playsInline style={{ display: 'none' }} />
      {/* Hidden video element used as audio fallback for browsers that handle video audio better */}
      {!isVideo && <video ref={remoteVideoRef} autoPlay playsInline style={{ display: 'none', width: 0, height: 0 }} />}

      {/* Reconnecting overlay */}
      <CallReconnectingOverlay isReconnecting={isReconnecting} />

      {/* Static gradient background - no distracting animations */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460]" />
      </div>

      {/* Video background */}
      {isVideo && remoteStream && (
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}

      {isVideo && remoteStream && (
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/20" />
      )}

      {/* Remote on hold banner */}
      {isRemoteOnHold && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute top-16 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 bg-amber-500/90 text-white px-4 py-2 rounded-full shadow-lg"
        >
          <Pause className="h-4 w-4" />
          <span className="text-sm font-medium">On Hold</span>
        </motion.div>
      )}

      {/* Local on hold banner */}
      {isOnHold && !isRemoteOnHold && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute top-16 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 bg-amber-500/90 text-white px-4 py-2 rounded-full shadow-lg"
        >
          <Pause className="h-4 w-4" />
          <span className="text-sm font-medium">Call on Hold</span>
        </motion.div>
      )}

      {/* Screen sharing banner */}
      {isScreenSharing && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute top-16 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 bg-primary/90 text-primary-foreground px-4 py-2 rounded-full shadow-lg"
        >
          <Monitor className="h-4 w-4" />
          <span className="text-sm font-medium">You are sharing your screen</span>
        </motion.div>
      )}

      {/* Top bar */}
      <div className="relative z-10 p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-full text-white hover:bg-white/10"
            onClick={handleBack}
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <span className="text-white font-semibold text-sm">{callerName}</span>
            <div className="flex items-center gap-1.5">
              <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-400/30 backdrop-blur-sm text-[10px] px-1.5 py-0">
                FREE
              </Badge>
              <span className="text-white/50 text-xs">{getStatusText()}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Call quality indicator */}
          {status === 'connected' && callQuality && (
            <CallQualityIndicator quality={callQuality} />
          )}
          {/* Encrypted badge - tappable for safety number */}
          <button
            onClick={() => setShowSafetyNumber(true)}
            className="flex items-center gap-1.5 text-white/40 text-xs hover:text-white/60 transition-colors"
          >
            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
            <span>Encrypted</span>
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center">
        {(!isVideo || !remoteStream) && (
          <>
            {status === 'ringing' && (
              <>
                <motion.div
                  animate={{ scale: [1, 1.5, 1], opacity: [0.2, 0, 0.2] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="absolute rounded-full bg-white/10"
                  style={{ width: 200, height: 200 }}
                />
                <motion.div
                  animate={{ scale: [1, 1.8, 1], opacity: [0.1, 0, 0.1] }}
                  transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                  className="absolute rounded-full bg-white/5"
                  style={{ width: 280, height: 280 }}
                />
              </>
            )}

            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 20 }}
            >
              <Avatar className="h-32 w-32 border-4 border-white/10 shadow-2xl mb-6 ring-4 ring-white/5 ring-offset-4 ring-offset-transparent">
                <AvatarImage src={callerAvatar || undefined} alt={callerName} />
                <AvatarFallback className="text-4xl bg-white/10 text-white backdrop-blur-sm">
                  {callerName.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </motion.div>

            <motion.h2
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="text-2xl font-bold text-white mb-1"
            >
              {callerName}
            </motion.h2>
            <motion.p
              initial={{ y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-white/50 font-medium"
            >
              {getStatusText()}
            </motion.p>
          </>
        )}
      </div>

      {/* Local video preview */}
      {isVideo && localStream && (
        <motion.div
          drag
          dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
          className="absolute bottom-32 right-4 z-20 w-28 h-40 rounded-2xl overflow-hidden shadow-2xl border-2 border-white/20"
        >
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
          />
          {!isVideoEnabled && (
            <div className="absolute inset-0 bg-slate-900/90 flex items-center justify-center backdrop-blur-sm">
              <VideoOff className="h-8 w-8 text-white/40" />
            </div>
          )}
        </motion.div>
      )}

      {/* Bottom controls */}
      <div className="relative z-10 p-6 pb-safe">
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3, type: 'spring' }}
          className="flex items-center justify-center gap-4"
        >
          <motion.div whileTap={{ scale: 0.9 }}>
            <Button
              variant="outline"
              size="icon"
              className={`h-14 w-14 rounded-full backdrop-blur-xl border-white/10 ${
                isMuted ? 'bg-destructive/30 text-destructive-foreground border-destructive/30' : 'bg-white/10 text-white'
              }`}
              onClick={onToggleMute}
            >
              {isMuted ? <MicOff className="h-6 w-6" /> : <Mic className="h-6 w-6" />}
            </Button>
          </motion.div>

          {isVideo && (
            <motion.div whileTap={{ scale: 0.9 }}>
              <Button
                variant="outline"
                size="icon"
                className={`h-14 w-14 rounded-full backdrop-blur-xl border-white/10 ${
                  !isVideoEnabled ? 'bg-destructive/30 text-destructive-foreground border-destructive/30' : 'bg-white/10 text-white'
                }`}
                onClick={onToggleVideo}
              >
                {isVideoEnabled ? <Video className="h-6 w-6" /> : <VideoOff className="h-6 w-6" />}
              </Button>
          </motion.div>
          )}

          {/* Flip camera - video calls, mobile only */}
          {isVideo && onFlipCamera && (
            <motion.div whileTap={{ scale: 0.9 }}>
              <Button
                variant="outline"
                size="icon"
                className="h-14 w-14 rounded-full backdrop-blur-xl border-white/10 bg-white/10 text-white"
                onClick={onFlipCamera}
              >
                <RotateCcw className="h-6 w-6" />
              </Button>
            </motion.div>
          )}

          {/* Hold/Resume */}
          {status === 'connected' && onToggleHold && (
            <motion.div whileTap={{ scale: 0.9 }}>
              <Button
                variant="outline"
                size="icon"
                className={`h-14 w-14 rounded-full backdrop-blur-xl border-white/10 ${
                  isOnHold ? 'bg-amber-500/30 text-amber-300 border-amber-400/30' : 'bg-white/10 text-white'
                }`}
                onClick={onToggleHold}
              >
                {isOnHold ? <Play className="h-6 w-6" /> : <Pause className="h-6 w-6" />}
              </Button>
            </motion.div>
          )}

          {/* Screen share - desktop only */}
          {isDesktop && isVideo && onToggleScreenShare && (
            <motion.div whileTap={{ scale: 0.9 }}>
              <Button
                variant="outline"
                size="icon"
                className={`h-14 w-14 rounded-full backdrop-blur-xl border-white/10 ${
                  isScreenSharing ? 'bg-primary/30 text-primary-foreground border-primary/30' : 'bg-white/10 text-white'
                }`}
                onClick={onToggleScreenShare}
              >
                {isScreenSharing ? <MonitorOff className="h-6 w-6" /> : <Monitor className="h-6 w-6" />}
              </Button>
            </motion.div>
          )}

          <motion.div whileTap={{ scale: 0.9 }}>
            <Button
              variant="destructive"
              size="icon"
              className="h-16 w-16 rounded-full shadow-[0_0_20px_rgba(239,68,68,0.4)]"
              onClick={onEndCall}
            >
              <PhoneOff className="h-7 w-7" />
            </Button>
          </motion.div>

          {/* Speaker toggle */}
          {onToggleSpeaker && (
            <motion.div whileTap={{ scale: 0.9 }}>
              <Button
                variant="outline"
                size="icon"
                className={`h-14 w-14 rounded-full backdrop-blur-xl border-white/10 ${
                  isSpeakerOn ? 'bg-primary/30 text-primary-foreground border-primary/30' : 'bg-white/10 text-white'
                }`}
                onClick={onToggleSpeaker}
              >
                {isSpeakerOn ? <Volume2 className="h-6 w-6" /> : <VolumeX className="h-6 w-6" />}
              </Button>
            </motion.div>
          )}

          <motion.div whileTap={{ scale: 0.9 }}>
            <Button
              variant="outline"
              size="icon"
              className="h-14 w-14 rounded-full backdrop-blur-xl border-white/10 bg-white/10 text-white"
              onClick={() => setIsChatOpen(true)}
            >
              <MessageCircle className="h-6 w-6" />
            </Button>
          </motion.div>

          <motion.div whileTap={{ scale: 0.9 }}>
            <Button
              variant="outline"
              size="icon"
              className="h-14 w-14 rounded-full backdrop-blur-xl border-white/10 bg-white/10 text-white"
            >
              <MoreVertical className="h-6 w-6" />
            </Button>
          </motion.div>
        </motion.div>
      </div>

      {/* In-call chat */}
      <ChatPanel
        isOpen={isChatOpen}
        onOpenChange={setIsChatOpen}
        roomId={chatRoomId || undefined}
      />

      {/* Safety number modal */}
      <SafetyNumberModal
        isOpen={showSafetyNumber}
        onClose={() => setShowSafetyNumber(false)}
        otherUserId={calleeId || null}
        currentUserId={currentUserId}
        otherUserName={callerName}
      />
    </motion.div>
  );
}
